import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as BABYLON from '@babylonjs/core'
import { ScenePresetId, StudioRoomState } from '../types'
import { ROOM_DEFAULTS, SCENE_PRESET_GEOMETRY } from '../constants'
import { applyStudioIbl, createOverheadDownLight } from '../sceneKit/galleryLightRig'

export interface StudioRoomApi {
  room: StudioRoomState
  setWallColor: (hex: string) => void
  setFloorColor: (hex: string) => void
  setCeilColor: (hex: string) => void
  setAmbience: (value: number) => void
  setFog: (value: number) => void
  setDwellSeconds: (value: number) => void
  setShowPins: (value: boolean) => void
  /** Showcase-only fake presence cursor ("Priya · demo") until realtime multiplayer exists. */
  setShowPresenceDemo: (value: boolean) => void
  /** Optional indoor IBL (Babylon specular env) — soft floor reflection until full PBR. */
  setUseHdri: (value: boolean) => void
  /** Swaps the room shell (dimensions, wall/floor defaults, light tint) to a different procedural preset — also switches `mode` back to `'studio'`. */
  setScenePreset: (preset: ScenePresetId) => void
  /** Switches to one of the 14 original "Classic" room shapes — see `classicCatalog.ts` and `useStudioLegacyRoom`. */
  setClassicTemplate: (templateId: number) => void
  /**
   * Dollhouse cutaway: walls/ceiling between the camera and the room fade out.
   * Pass a getter (not a snapshot) so orbit works without clicking Plan first —
   * cameras are created after the first React paint and refs must be read live.
   * Classic rooms have no cutaway.
   */
  registerActiveCamera: (getInfo: () => { camera: BABYLON.Camera | null; isInside: boolean }) => void
  /** Editor floor grid: brighter in plan (top-down CAD read), dim in orbit, hidden entirely in walk (guest view). */
  setGridMode: (view: 'orbit' | 'plan' | 'walk') => void
  /** Full room replace for undo/redo / shared apply path — shell rebuilds when mode/preset change. */
  applyRoomState: (next: StudioRoomState) => void
}

/** Builds the room shell (walls/floor/ceiling/grid/lights) for a scene preset and exposes live setters for the inspector. */
export function useStudioRoom(scene: BABYLON.Scene | null, initial: StudioRoomState = ROOM_DEFAULTS): StudioRoomApi {
  const [room, setRoom] = useState<StudioRoomState>(initial)
  const roomRef = useRef(room)
  roomRef.current = room
  const matsRef = useRef<{
    wall: BABYLON.StandardMaterial
    floor: BABYLON.StandardMaterial
    ceil: BABYLON.StandardMaterial
    ambient: BABYLON.HemisphericLight
    fill: BABYLON.HemisphericLight
  } | null>(null)
  const gridRef = useRef<BABYLON.LinesMesh | null>(null)
  const getActiveCameraRef = useRef<() => { camera: BABYLON.Camera | null; isInside: boolean }>(
    () => ({ camera: null, isInside: false }),
  )
  /** Frames to skip dollhouse cutaway after a shell rebuild (preset swap). */
  const cutawayGraceRef = useRef(0)
  // Rebuilding the shell on every preset switch resets wall/floor color to
  // that preset's defaults (see constants.ts doc comment) — but the very
  // first build should still honor a saved/customized color, not the
  // preset's stock one. Only the initial mount reads from `initial`.
  const hasMountedRef = useRef(false)
  // After hydrate, `setRoom(seed)` can change `scenePreset`/`mode` and
  // re-fire this effect. That echo must NOT treat the rebuild like a user
  // preset swap (which stamps stock dark corridor/loft finishes) — that's
  // the "scene looks right, then snaps dark" flash on refresh.
  const hydrateEchoRef = useRef(false)
  const shellKey = `${room.mode || 'studio'}:${room.scenePreset || 'corridor'}`

  useEffect(() => {
    if (!scene) {
      // Engine tear-down (seedReady flicker / gallery switch) — next scene
      // must re-hydrate from `initial` or Ambience/Haze stay on the stale
      // mount-time ROOM_DEFAULTS snapshot.
      hasMountedRef.current = false
      hydrateEchoRef.current = false
      return
    }

    const live = roomRef.current
    const seed = { ...ROOM_DEFAULTS, ...initial }
    const isFirstBuild = !hasMountedRef.current
    // Hydrate-echo rebuilds must keep seed finishes (not stamp preset stock).
    const useSeedFinishes = isFirstBuild || hydrateEchoRef.current
    if (hydrateEchoRef.current) hydrateEchoRef.current = false

    if (isFirstBuild) {
      hasMountedRef.current = true
      const prevPreset = live.scenePreset || 'corridor'
      const nextPreset = seed.scenePreset || 'corridor'
      const prevMode = live.mode || 'studio'
      const nextMode = seed.mode || 'studio'
      setRoom(seed)
      if (prevPreset !== nextPreset || prevMode !== nextMode) {
        hydrateEchoRef.current = true
      }
    }

    // Classic mode replaces this entire procedural shell with a loaded
    // `.babylon` file (see `useStudioLegacyRoom`) — nothing to build here.
    // The cleanup from whichever build ran before this (studio -> classic
    // switch) still fires normally; this effect just doesn't create a new one.
    const activeMode = (isFirstBuild ? seed.mode : live.mode) || 'studio'
    if (activeMode === 'classic') {
      scene.fogDensity = (isFirstBuild ? seed.fog : live.fog) ?? ROOM_DEFAULTS.fog
      return
    }

    // Prefer the shellKey-driven live preset (user Corridor/Loft/Pavilion pick).
    // First paint may still use seed when React state hasn't hydrated yet.
    const presetId = ((isFirstBuild ? seed.scenePreset : live.scenePreset) || 'corridor') as keyof typeof SCENE_PRESET_GEOMETRY
    const preset = SCENE_PRESET_GEOMETRY[presetId] || SCENE_PRESET_GEOMETRY.corridor
    const { width: W, height: H, depth: D } = preset
    // Finishes come from room state (not preset geometry defaults) so undo/redo
    // can restore custom wall/floor hexes. `setScenePreset` already stamps stock
    // colors into `room` on a user swap.
    const wallColor = useSeedFinishes ? seed.wallColor : live.wallColor
    const floorColor = useSeedFinishes ? seed.floorColor : live.floorColor
    const wallMat = new BABYLON.StandardMaterial('studio-wall', scene)
    wallMat.diffuseColor = BABYLON.Color3.FromHexString(wallColor)
    wallMat.specularColor = BABYLON.Color3.Black()
    wallMat.backFaceCulling = false
    wallMat.twoSidedLighting = true
    // Ambient + fill + print spots + overheads — keep headroom so the
    // Ambience slider still washes walls after Loft/Pavilion adds more cans.
    wallMat.maxSimultaneousLights = 20

    const floorMat = new BABYLON.StandardMaterial('studio-floor', scene)
    floorMat.diffuseColor = BABYLON.Color3.FromHexString(floorColor)
    floorMat.specularColor = BABYLON.Color3.FromHexString(preset.floorSpecular)
    floorMat.specularPower = 140
    floorMat.maxSimultaneousLights = 20

    // Falls back to the preset's stock tone for saves made before ceiling
    // color existed (see StudioRoomState.ceilColor doc comment).
    const ceilColor = (useSeedFinishes ? seed.ceilColor : live.ceilColor) || preset.ceilColor
    const ceilMat = new BABYLON.StandardMaterial('studio-ceil', scene)
    ceilMat.diffuseColor = BABYLON.Color3.FromHexString(ceilColor)
    ceilMat.specularColor = BABYLON.Color3.Black()
    ceilMat.backFaceCulling = false
    ceilMat.twoSidedLighting = true
    ceilMat.maxSimultaneousLights = 20

    const floor = BABYLON.MeshBuilder.CreateGround('studio-floor-mesh', { width: W, height: D }, scene)
    floor.material = floorMat

    const ceil = BABYLON.MeshBuilder.CreatePlane('studio-ceil-mesh', { width: W, height: D }, scene)
    ceil.rotation.x = -Math.PI / 2
    ceil.position.y = H
    ceil.material = ceilMat
    ceil.isPickable = false

    const wallsInfo: { mesh: BABYLON.Mesh; normal: BABYLON.Vector3; position: BABYLON.Vector3 }[] = []
    const makeWall = (name: string, width: number, position: BABYLON.Vector3, rotationY: number, inward: BABYLON.Vector3) => {
      const plane = BABYLON.MeshBuilder.CreatePlane(name, { width, height: H }, scene)
      plane.position.copyFrom(position)
      plane.rotation.y = rotationY
      plane.material = wallMat
      wallsInfo.push({ mesh: plane, normal: inward, position: position.clone() })
      return plane
    }
    makeWall('wall_left', D, new BABYLON.Vector3(-W / 2, H / 2, 0), Math.PI / 2, new BABYLON.Vector3(1, 0, 0))
    makeWall('wall_right', D, new BABYLON.Vector3(W / 2, H / 2, 0), -Math.PI / 2, new BABYLON.Vector3(-1, 0, 0))
    makeWall('wall_back', W, new BABYLON.Vector3(0, H / 2, D / 2), Math.PI, new BABYLON.Vector3(0, 0, -1))
    makeWall('wall_entry', W, new BABYLON.Vector3(0, H / 2, -D / 2), 0, new BABYLON.Vector3(0, 0, 1))

    // Ceiling dressing + downlights that fire from each fixture aperture
    // (not omni points that wash the volume). Cutaway fades intensity with ceil.
    const lamps: BABYLON.Mesh[] = []
    const overheadLights: BABYLON.SpotLight[] = []
    const extras: BABYLON.Mesh[] = []
    const extraMats: BABYLON.StandardMaterial[] = []
    const lampGlow = new BABYLON.StandardMaterial('studio-lamp', scene)
    lampGlow.emissiveColor = BABYLON.Color3.FromHexString(preset.lampColor)
    lampGlow.disableLighting = true
    const lampDiffuse = BABYLON.Color3.FromHexString(preset.lampColor)

    if (preset.structure === 'columns') {
      // Loft — pendant bulbs; warm cone leaves the bulb downward.
      const wireMat = new BABYLON.StandardMaterial('studio-pendant-wire', scene)
      wireMat.diffuseColor = BABYLON.Color3.FromHexString('#0c0a08')
      wireMat.specularColor = BABYLON.Color3.Black()
      extraMats.push(wireMat)
      const bulbMat = new BABYLON.StandardMaterial('studio-pendant-bulb', scene)
      bulbMat.emissiveColor = BABYLON.Color3.FromHexString(preset.lampColor).scale(0.55)
      bulbMat.disableLighting = true
      extraMats.push(bulbMat)

      const pendantSpan = D / 2 - 3
      let pendantIdx = 0
      for (let z = -pendantSpan; z <= pendantSpan + 0.001; z += 3.2) {
        const wire = BABYLON.MeshBuilder.CreateCylinder('studio-pendant-wire', { diameter: 0.012, height: 0.7 }, scene)
        wire.position.set(0, H - 0.35, z)
        wire.material = wireMat
        wire.isPickable = false
        const bulb = BABYLON.MeshBuilder.CreateSphere('studio-pendant-bulb', { diameter: 0.14 }, scene)
        bulb.position.set(0, H - 0.7, z)
        bulb.material = bulbMat
        bulb.isPickable = false
        lamps.push(wire, bulb)
        const light = createOverheadDownLight(scene, `studio-overhead-pendant-${pendantIdx++}`, {
          position: new BABYLON.Vector3(0, H - 0.78, z),
          diffuse: lampDiffuse,
          intensity: 2.4,
          range: 10,
          angle: Math.PI / 2.6,
        })
        overheadLights.push(light)
      }
      const colMat = new BABYLON.StandardMaterial('studio-column', scene)
      colMat.diffuseColor = BABYLON.Color3.FromHexString('#1c140e')
      colMat.specularColor = BABYLON.Color3.Black()
      extraMats.push(colMat)
      ;[[-1, -1], [-1, 1], [1, -1], [1, 1]].forEach(([sx, sz]) => {
        const col = BABYLON.MeshBuilder.CreateBox('studio-column-mesh', { width: 0.32, height: H, depth: 0.32 }, scene)
        col.position.set(sx * (W / 2 - 1.6), H / 2, sz * (D / 2 - 2.4))
        col.material = colMat
        col.isPickable = false
        extras.push(col)
      })
    } else if (preset.structure === 'skylight') {
      // Pavilion — glass roof; cool daylight cones drop from the aperture.
      const skyMat = new BABYLON.StandardMaterial('studio-skylight-mat', scene)
      skyMat.emissiveColor = BABYLON.Color3.FromHexString(preset.lampColor).scale(0.4)
      skyMat.disableLighting = true
      extraMats.push(skyMat)

      const skyW = W * 0.5
      const skyD = D * 0.5
      const sky = BABYLON.MeshBuilder.CreatePlane('studio-skylight', { width: skyW, height: skyD }, scene)
      sky.rotation.x = -Math.PI / 2
      sky.position.set(0, H - 0.02, 0)
      sky.material = skyMat
      sky.isPickable = false
      lamps.push(sky)

      const mullionMat = new BABYLON.StandardMaterial('studio-mullion', scene)
      mullionMat.diffuseColor = BABYLON.Color3.FromHexString('#9a9184')
      mullionMat.specularColor = BABYLON.Color3.Black()
      mullionMat.disableLighting = true
      extraMats.push(mullionMat)
      for (let i = -1; i <= 1; i++) {
        const bar = BABYLON.MeshBuilder.CreateBox('studio-mullion-mesh', { width: skyW + 0.05, height: 0.02, depth: 0.03 }, scene)
        bar.position.set(0, H - 0.015, i * (skyD / 3))
        bar.material = mullionMat
        bar.isPickable = false
        lamps.push(bar)
      }

      const skyDiffuse = BABYLON.Color3.FromHexString('#d4dce8')
      ;[
        new BABYLON.Vector3(0, H - 0.2, 0),
        new BABYLON.Vector3(0, H - 0.2, skyD * 0.28),
        new BABYLON.Vector3(0, H - 0.2, -skyD * 0.28),
      ].forEach((pos, i) => {
        const light = createOverheadDownLight(scene, `studio-overhead-skylight-${i}`, {
          position: pos,
          diffuse: skyDiffuse,
          intensity: 1.8,
          range: 18,
          angle: Math.PI / 2.1,
        })
        overheadLights.push(light)
      })
    } else {
      // Corridor — flush cans; beam leaves the disc straight down.
      const lampSpan = D / 2 - 2
      let lampIdx = 0
      for (let z = -lampSpan; z <= lampSpan + 0.001; z += 3) {
        const disc = BABYLON.MeshBuilder.CreateDisc('studio-lamp-mesh', { radius: 0.11 }, scene)
        disc.rotation.x = -Math.PI / 2
        disc.position.set(0, H - 0.03, z)
        disc.material = lampGlow
        disc.isPickable = false
        lamps.push(disc)
        const light = createOverheadDownLight(scene, `studio-overhead-can-${lampIdx++}`, {
          position: new BABYLON.Vector3(0, H - 0.08, z),
          diffuse: lampDiffuse,
          intensity: 2.1,
          range: 9,
          angle: Math.PI / 3,
        })
        overheadLights.push(light)
      }
    }

    const stripMat = new BABYLON.StandardMaterial('studio-strip', scene)
    stripMat.emissiveColor = BABYLON.Color3.FromHexString(preset.stripColor)
    stripMat.disableLighting = true
    const strips: BABYLON.Mesh[] = [-1, 1].map((s) => {
      const strip = BABYLON.MeshBuilder.CreateBox('studio-strip-mesh', { width: 0.03, height: 0.02, depth: D }, scene)
      strip.position.set(s * (W / 2 - 0.02), 0.04, 0)
      strip.material = stripMat
      strip.isPickable = false
      return strip
    })

    const gridLines: BABYLON.Vector3[][] = []
    for (let x = -W / 2; x <= W / 2; x += 1) {
      gridLines.push([new BABYLON.Vector3(x, 0.012, -D / 2), new BABYLON.Vector3(x, 0.012, D / 2)])
    }
    for (let z = -D / 2; z <= D / 2; z += 1) {
      gridLines.push([new BABYLON.Vector3(-W / 2, 0.012, z), new BABYLON.Vector3(W / 2, 0.012, z)])
    }
    const grid = BABYLON.MeshBuilder.CreateLineSystem('studio-grid', { lines: gridLines }, scene)
    grid.color = BABYLON.Color3.FromHexString('#be9dfd')
    grid.alpha = 0.05
    grid.isPickable = false
    gridRef.current = grid

    const liveAmb = roomRef.current.ambience
    const ambient = new BABYLON.HemisphericLight('studio-ambient', new BABYLON.Vector3(0, 1, 0), scene)
    ambient.diffuse = BABYLON.Color3.FromHexString(preset.ambientDiffuse)
    ambient.groundColor = BABYLON.Color3.FromHexString(preset.ambientGround)
    ambient.intensity = liveAmb

    // Upward hemi = ground bounce / fill. A PointLight fell off hard in the
    // wider Loft and left walls near-black even when Ambience carried at 1.30.
    const fill = new BABYLON.HemisphericLight('studio-fill', new BABYLON.Vector3(0, -1, 0), scene)
    fill.diffuse = BABYLON.Color3.FromHexString(preset.fillColor)
    fill.groundColor = BABYLON.Color3.Black()
    fill.intensity = 0.35 + liveAmb * 0.7

    // Downlights are floor pools — keep them off wall materials so they don't
    // crowd out ambient/fill under StandardMaterial's simultaneous-light cap
    // (Corridor→Loft with 9 print spots was going fully black at max Ambience).
    overheadLights.forEach((light) => {
      light.includedOnlyMeshes.push(floor)
    })

    scene.fogDensity = roomRef.current.fog

    matsRef.current = { wall: wallMat, floor: floorMat, ceil: ceilMat, ambient, fill }
    cutawayGraceRef.current = 45

    const cutawayObserver = scene.onBeforeRenderObservable.add(() => {
      if (cutawayGraceRef.current > 0) {
        cutawayGraceRef.current -= 1
        wallsInfo.forEach((wi) => {
          wi.mesh.visibility = 1
          wi.mesh.isPickable = true
        })
        ceil.visibility = 1
        lamps.forEach((l) => {
          l.visibility = 1
        })
        return
      }
      // Live getter — orbit/plan share the same cutaway; walk reports isInside.
      const { camera, isInside } = getActiveCameraRef.current()
      const camPos = camera ? camera.globalPosition ?? camera.position : null
      if (!camPos) return
      // Fade any wall whose exterior faces the camera (same dollhouse as Plan).
      wallsInfo.forEach((wi) => {
        const outside =
          !isInside && BABYLON.Vector3.Dot(camPos.subtract(wi.position), wi.normal) < 0
        const target = outside ? 0 : 1
        wi.mesh.visibility += (target - wi.mesh.visibility) * 0.28
        wi.mesh.isPickable = wi.mesh.visibility > 0.5
      })
      const ceilTarget = !isInside && camPos.y > H - 0.4 ? 0 : 1
      ceil.visibility += (ceilTarget - ceil.visibility) * 0.28
      // Fade fixture *meshes* for dollhouse cutaway only — never kill the
      // actual lights (that made Corridor→Loft swaps go fully dark in orbit).
      lamps.forEach((l) => {
        l.visibility = ceil.visibility
      })
    })

    return () => {
      scene.onBeforeRenderObservable.remove(cutawayObserver)
      floor.dispose()
      ceil.dispose()
      grid.dispose()
      wallsInfo.forEach((wi) => wi.mesh.dispose())
      lamps.forEach((l) => l.dispose())
      overheadLights.forEach((l) => l.dispose())
      extras.forEach((e) => e.dispose())
      extraMats.forEach((m) => m.dispose())
      strips.forEach((s) => s.dispose())
      wallMat.dispose()
      floorMat.dispose()
      ceilMat.dispose()
      lampGlow.dispose()
      stripMat.dispose()
      ambient.dispose()
      fill.dispose()
      matsRef.current = null
      gridRef.current = null
    }
    // Shell rebuilds only when scene / preset / mode change — not on every
    // ambience tick. Atmosphere is pushed onto the live lights below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, shellKey])

  // Keep hemispheric + fill + fog glued to the slider after preset swaps
  // (new lights are created in the shell effect; this re-applies the carried value).
  useEffect(() => {
    if (!scene || (room.mode || 'studio') === 'classic') return
    const mats = matsRef.current
    if (!mats) return
    mats.ambient.intensity = room.ambience
    mats.fill.intensity = 0.35 + room.ambience * 0.7
    scene.fogDensity = room.fog
  }, [scene, room.ambience, room.fog, room.mode, shellKey])

  // Optional indoor IBL — toggles without rebuilding the shell (would stomp finishes).
  useEffect(() => {
    if (!scene || room.mode === 'classic') return
    const mats = matsRef.current
    if (!mats) return
    if (!room.useHdri) {
      if (scene.environmentTexture) {
        const env = scene.environmentTexture
        scene.environmentTexture = null
        if (mats.floor.reflectionTexture === env) mats.floor.reflectionTexture = null
        env.dispose()
      }
      return
    }
    const disposeIbl = applyStudioIbl(scene, mats.floor, 0.55)
    return () => disposeIbl()
  }, [scene, room.useHdri, room.mode, shellKey])

  const setWallColor = useCallback((hex: string) => {
    if (matsRef.current) matsRef.current.wall.diffuseColor = BABYLON.Color3.FromHexString(hex)
    setRoom((r) => ({ ...r, wallColor: hex }))
  }, [])

  const setFloorColor = useCallback((hex: string) => {
    if (matsRef.current) matsRef.current.floor.diffuseColor = BABYLON.Color3.FromHexString(hex)
    setRoom((r) => ({ ...r, floorColor: hex }))
  }, [])

  const setCeilColor = useCallback((hex: string) => {
    if (matsRef.current) matsRef.current.ceil.diffuseColor = BABYLON.Color3.FromHexString(hex)
    setRoom((r) => ({ ...r, ceilColor: hex }))
  }, [])

  const setAmbience = useCallback((value: number) => {
    const mats = matsRef.current
    if (mats) {
      mats.ambient.intensity = value
      mats.fill.intensity = 0.35 + value * 0.7
    }
    setRoom((r) => ({ ...r, ambience: value }))
  }, [])

  const setFog = useCallback(
    (value: number) => {
      if (scene) scene.fogDensity = value
      setRoom((r) => ({ ...r, fog: value }))
    },
    [scene],
  )

  const setScenePreset = useCallback((preset: ScenePresetId) => {
    const geo = SCENE_PRESET_GEOMETRY[preset]
    // Shell finishes + dimensions reset with the scene; Ambience/Haze stay
    // on the user's slider so atmosphere carries Corridor ↔ Loft ↔ Pavilion
    // and the hemispheric light keeps matching the inspector.
    //
    // width/height/depth MUST come along too: `room` is the only source of
    // truth callers outside this hook (useStudioArts, for print/spotlight
    // placement) have for the room's current dimensions.
    setRoom((r) => ({
      ...r,
      mode: 'studio',
      scenePreset: preset,
      width: geo.width,
      height: geo.height,
      depth: geo.depth,
      wallColor: geo.wallColor,
      floorColor: geo.floorColor,
      ceilColor: geo.ceilColor,
    }))
  }, [])

  /**
   * Switches to one of the 14 original "Classic" room shapes. This hook only
   * flips `mode`/`classicTemplateId` in state — the actual `.babylon` load,
   * wall retint, and print texturing all happen in `useStudioLegacyRoom`,
   * which the caller (Studio3DScreen) wires to these same state fields.
   */
  const setClassicTemplate = useCallback((templateId: number) => {
    setRoom((r) => ({ ...r, mode: 'classic', classicTemplateId: templateId }))
  }, [])

  const setDwellSeconds = useCallback((value: number) => setRoom((r) => ({ ...r, dwellSeconds: value })), [])
  const setShowPins = useCallback((value: boolean) => setRoom((r) => ({ ...r, showPins: value })), [])
  const setShowPresenceDemo = useCallback((value: boolean) => setRoom((r) => ({ ...r, showPresenceDemo: value })), [])
  const setUseHdri = useCallback((value: boolean) => setRoom((r) => ({ ...r, useHdri: value })), [])

  const registerActiveCamera = useCallback(
    (getInfo: () => { camera: BABYLON.Camera | null; isInside: boolean }) => {
      getActiveCameraRef.current = getInfo
    },
    [],
  )

  const setGridMode = useCallback((view: 'orbit' | 'plan' | 'walk') => {
    const grid = gridRef.current
    if (!grid) return
    grid.setEnabled(view !== 'walk')
    grid.alpha = view === 'plan' ? 0.16 : 0.05
  }, [])

  const applyRoomState = useCallback(
    (next: StudioRoomState) => {
      const merged = { ...ROOM_DEFAULTS, ...next }
      setRoom(merged)
      const mats = matsRef.current
      if (mats) {
        mats.wall.diffuseColor = BABYLON.Color3.FromHexString(merged.wallColor)
        mats.floor.diffuseColor = BABYLON.Color3.FromHexString(merged.floorColor)
        if (merged.ceilColor) mats.ceil.diffuseColor = BABYLON.Color3.FromHexString(merged.ceilColor)
        mats.ambient.intensity = merged.ambience
        mats.fill.intensity = 0.35 + merged.ambience * 0.7
      }
      if (scene) scene.fogDensity = merged.fog
    },
    [scene],
  )

  return useMemo(
    () => ({
      room,
      setWallColor,
      setFloorColor,
      setCeilColor,
      setAmbience,
      setFog,
      setDwellSeconds,
      setShowPins,
      setShowPresenceDemo,
      setUseHdri,
      setScenePreset,
      setClassicTemplate,
      registerActiveCamera,
      setGridMode,
      applyRoomState,
    }),
    [
      room,
      setWallColor,
      setFloorColor,
      setCeilColor,
      setAmbience,
      setFog,
      setDwellSeconds,
      setShowPins,
      setShowPresenceDemo,
      setUseHdri,
      setScenePreset,
      setClassicTemplate,
      registerActiveCamera,
      setGridMode,
      applyRoomState,
    ],
  )
}
